import React, { useEffect, useRef, useState } from "react";
import { MdGridView } from "react-icons/md";
import { IoClose, IoSettingsOutline } from "react-icons/io5";
import { LuVolume1, LuVolume2, LuVolumeOff } from "react-icons/lu";
import { useLiveData } from "../../../../hooks/useLiveData";
import { useRemote } from "../../../../hooks/useRemote";
import useGlobalStore from "../../../../store/global.store";
import useVipMultiviewSoundStore from "../../../../store/useVipMultiviewSoundStore";
import YouTubePlayer from "../YouTubePlayer";
import {
  PRE_RECORDED_NAMES,
  TOP_STREAMERS,
  BLOCKED_VIDEO_IDS,
  getBackgroundByEST,
} from "../multi-view.utils";
import { globalStyles } from "../../../../utils/global-styles";

const MultiViewLandingTV = () => {
  const { liveStreams, addEndedStream } = useLiveData();
  const { isMuted, toggleMute, increaseVolume, decreaseVolume, setMuted } =
    useVipMultiviewSoundStore();
  const { updateView } = useRemote();
  const [totalPlayers, setTotalPlayers] = useState(4);
  // Calculate grid dimensions based on total players
  const gridSize = totalPlayers <= 2 ? 2 : Math.ceil(Math.sqrt(totalPlayers));

  const setShowBottomBar = useGlobalStore((state) => state.setShowBottomBar);
  const [showGridConfigModal, setShowGridConfigModal] = useState(false);
  const [selectedGridOption, setSelectedGridOption] = useState(0);

  const [videoIds, setVideoIds] = useState(Array(totalPlayers).fill(""));
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [currentVideoIds, setCurrentVideoIds] = useState(
    Array(totalPlayers).fill("")
  );
  const [recentlyEndedIds, setRecentlyEndedIds] = useState([]);
  const [fullscreenIndex, setFullscreenIndex] = useState(null);
  const [showControls, setShowControls] = useState(true);

  const [backgroundImage, setBackgroundImage] = useState(getBackgroundByEST());
  const [endedIndexes, setEndedIndexes] = useState([]);
  const [allThumbnails, setAllThumbnails] = useState([]);
  const [showVideoThumbnails, setShowVideoThumbnails] = useState(false);
  const [selectedThumbnailIndex, setSelectedThumbnailIndex] = useState(0);
  // Track whether the focus change is coming from user interaction or programmatic
  const isUserFocus = useRef(true);

  const hasInteracted = useRef(false);
  const lastTapRef = useRef(0);

  // Refs for thumbnail focus management
  const thumbnailRefs = useRef([]);
  // Refs for video grid focus management
  const videoGridRefs = useRef([]);

  useEffect(() => {
    setShowBottomBar(false);
    setMuted(false);
    const interval = setInterval(() => {
      setBackgroundImage(getBackgroundByEST());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Grid config modal navigation has highest priority
      if (showGridConfigModal) {
        switch (e.key) {
          case "ArrowLeft":
            setSelectedGridOption((prev) => Math.max(0, prev - 1));
            break;
          case "ArrowRight":
            setSelectedGridOption((prev) => Math.min(4, prev + 1));
            break;
          case "Enter":
            if (selectedGridOption === 0) {
              setTotalPlayers(4); // 4 players (2x2)
              const previousCount = totalPlayers;
              const newIds = Array(4).fill("");

              // Preserve existing videos
              videoIds.forEach((id, i) => {
                if (i < 4) newIds[i] = id;
              });

              // Auto-populate new slots if available
              if (previousCount < 4) {
                const availableIds = getAvailableVideoIds(
                  videoIds.filter((id) => id)
                );
                for (let i = previousCount; i < 4; i++) {
                  if (!newIds[i] && availableIds.length > 0) {
                    newIds[i] = availableIds.shift();
                  }
                }
              }

              setVideoIds(newIds);
              setCurrentVideoIds(newIds);
            } else if (selectedGridOption === 1) {
              setTotalPlayers(3); // 3 players
              const previousCount = totalPlayers;
              const newIds = Array(3).fill("");

              // Preserve existing videos
              videoIds.forEach((id, i) => {
                if (i < 3) newIds[i] = id;
              });

              // Auto-populate new slots if available
              if (previousCount < 3) {
                const availableIds = getAvailableVideoIds(
                  videoIds.filter((id) => id)
                );
                for (let i = previousCount; i < 3; i++) {
                  if (!newIds[i] && availableIds.length > 0) {
                    newIds[i] = availableIds.shift();
                  }
                }
              }

              setVideoIds(newIds);
              setCurrentVideoIds(newIds);
            } else if (selectedGridOption === 2) {
              setTotalPlayers(2); // 2 players
              const previousCount = totalPlayers;
              const newIds = Array(2).fill("");

              // Preserve existing videos
              videoIds.forEach((id, i) => {
                if (i < 2) newIds[i] = id;
              });

              // Auto-populate new slots if available
              if (previousCount < 2) {
                const availableIds = getAvailableVideoIds(
                  videoIds.filter((id) => id)
                );
                for (let i = previousCount; i < 2; i++) {
                  if (!newIds[i] && availableIds.length > 0) {
                    newIds[i] = availableIds.shift();
                  }
                }
              }

              setVideoIds(newIds);
              setCurrentVideoIds(newIds);
            } else if (selectedGridOption === 3) {
              setTotalPlayers(1); // 1 player (fullscreen)
              const newIds = Array(1).fill("");

              // Use the currently selected video if it exists
              if (videoIds[selectedIndex]) {
                newIds[0] = videoIds[selectedIndex];
              } else {
                // Or get a new video if available
                const availableIds = getAvailableVideoIds([]);
                if (availableIds.length > 0) {
                  newIds[0] = availableIds[0];
                }
              }

              setVideoIds(newIds);
              setCurrentVideoIds(newIds);
            } else if (selectedGridOption === 4) {
              // Close
            }
            console.log("Selected Grid Option: ", selectedGridOption);
            setShowGridConfigModal(false);
            setShowVideoThumbnails(false);
            break;
          case "Escape":
            setShowGridConfigModal(false);
            break;
          default:
            break;
        }
      } else if (showVideoThumbnails) {
        // Thumbnail navigation mode
        const maxIndex = allThumbnails.length + 2; // +3 for vip_exit, close, settings, -1 for zero-based
        e.preventDefault();
        switch (e.key) {
          case "ArrowLeft":
            setSelectedThumbnailIndex((prev) => {
              const newIndex = Math.max(0, prev - 1);
              // Focus the corresponding thumbnail button
              if (thumbnailRefs.current[newIndex]) {
                thumbnailRefs.current[newIndex].focus();
                thumbnailRefs.current[newIndex].scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                  inline: "center",
                });
              }
              return newIndex;
            });
            break;
          case "ArrowRight":
            setSelectedThumbnailIndex((prev) => {
              const newIndex = Math.min(maxIndex, prev + 1);
              // Focus the corresponding thumbnail button
              if (thumbnailRefs.current[newIndex]) {
                thumbnailRefs.current[newIndex].focus();
                thumbnailRefs.current[newIndex].scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                  inline: "center",
                });
              }
              return newIndex;
            });
            break;
          case "Enter":
            if (selectedThumbnailIndex === 0) {
              updateView("VIP", () => {
                setShowBottomBar(true);
              });
              break;
            }

            if (selectedThumbnailIndex === 1) {
              setShowVideoThumbnails(false);
              break;
            }
            if (selectedThumbnailIndex === 2) {
              console.log("settings");
              setShowVideoThumbnails(false);
              setShowGridConfigModal(true);
              setSelectedGridOption(0);
              break;
            }
            if (
              allThumbnails[selectedThumbnailIndex - 3] &&
              !currentVideoIds.includes(
                allThumbnails[selectedThumbnailIndex - 3].videoId
              )
            ) {
              handleVideoSelect(
                allThumbnails[selectedThumbnailIndex - 3].videoId
              );
              setShowVideoThumbnails(false);
            }
            break;
          case "Escape":
            setShowVideoThumbnails(false);
            break;
          default:
            break;
        }
      } else {
        // Video grid navigation mode
        switch (e.key) {
          case "ArrowLeft":
            setSelectedIndex((prev) => {
              const newIndex = prev % gridSize !== 0 ? prev - 1 : prev;
              if (videoGridRefs.current[newIndex]) {
                videoGridRefs.current[newIndex].focus();
              }
              return newIndex;
            });
            break;
          case "ArrowRight":
            setSelectedIndex((prev) => {
              const newIndex =
                (prev + 1) % gridSize !== 0 && prev < totalPlayers - 1
                  ? prev + 1
                  : prev;
              if (videoGridRefs.current[newIndex]) {
                videoGridRefs.current[newIndex].focus();
              }
              return newIndex;
            });
            break;
          case "ArrowUp":
            setSelectedIndex((prev) => {
              const newIndex = prev >= gridSize ? prev - gridSize : prev;
              if (videoGridRefs.current[newIndex]) {
                videoGridRefs.current[newIndex].focus();
              }
              return newIndex;
            });
            break;
          case "ArrowDown":
            setSelectedIndex((prev) => {
              // Special case for 3-player layout when on top-right video (index 1)
              if (totalPlayers === 3 && prev === 1) {
                const newIndex = 2; // Move to the bottom video (index 2)
                if (videoGridRefs.current[newIndex]) {
                  videoGridRefs.current[newIndex].focus();
                }
                return newIndex;
              }

              // Normal grid navigation
              const newIndex =
                prev < totalPlayers - gridSize ? prev + gridSize : prev;
              if (videoGridRefs.current[newIndex]) {
                videoGridRefs.current[newIndex].focus();
              }
              return newIndex;
            });
            break;
          case "Enter":
            setShowVideoThumbnails(true);
            setSelectedThumbnailIndex(0);
            break;
          default:
            break;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    showVideoThumbnails,
    selectedThumbnailIndex,
    allThumbnails,
    currentVideoIds,
    showGridConfigModal,
    selectedGridOption,
    totalPlayers,
    gridSize,
    videoIds,
  ]);

  // Focus management for thumbnail mode
  useEffect(() => {
    // Only proceed if we should manage focus
    const shouldFocus =
      showVideoThumbnails && thumbnailRefs.current[selectedThumbnailIndex];

    if (shouldFocus) {
      // Mark that this is a programmatic focus change
      isUserFocus.current = false;

      // Use requestAnimationFrame to ensure the UI has settled
      const animFrameId = requestAnimationFrame(() => {
        const timeoutId = setTimeout(() => {
          // Check if the element isn't already focused
          if (
            document.activeElement !==
            thumbnailRefs.current[selectedThumbnailIndex]
          ) {
            thumbnailRefs.current[selectedThumbnailIndex]?.focus();
          }

          // Re-enable user focus tracking after focus is complete
          isUserFocus.current = true;
        }, 150);

        return () => clearTimeout(timeoutId);
      });

      return () => {
        cancelAnimationFrame(animFrameId);
        isUserFocus.current = true;
      };
    }
  }, [showVideoThumbnails, selectedThumbnailIndex]);

  // Focus management for video grid mode
  useEffect(() => {
    if (
      !showVideoThumbnails &&
      !showGridConfigModal &&
      videoGridRefs.current[selectedIndex]
    ) {
      setTimeout(() => {
        videoGridRefs.current[selectedIndex]?.focus();
      }, 100);
    }
  }, [showVideoThumbnails, showGridConfigModal, selectedIndex]);

  const toggleControls = () => {
    setShowControls((prev) => {
      localStorage.setItem("controlsVisible", !prev);
      return !prev;
    });
  };

  const videoIdToStreamer = Object.entries(liveStreams || {}).reduce(
    (acc, [streamer, data]) => {
      if (data?.videoId) acc[data.videoId] = data.name || streamer;
      return acc;
    },
    {}
  );

  const getSortedThumbnails = (streams) => {
    if (!streams) return [];

    return Object.entries(streams)
      .filter(
        ([name, data]) => data?.videoId && !BLOCKED_VIDEO_IDS.has(data.videoId)
      )
      .map(([name, data]) => ({
        streamer: data.name,
        videoId: data.videoId,
        title: data.title,
        thumbnail: data.thumbnail,
        isTop: TOP_STREAMERS.has(name),
        isLoop: PRE_RECORDED_NAMES.has(name),
      }))
      .sort((a, b) => {
        if (a.isTop !== b.isTop) return b.isTop - a.isTop;
        if (a.isLoop !== b.isLoop) return a.isLoop - b.isLoop;
        return 0;
      });
  };

  const getAvailableVideoIds = (currentPlaying = []) => {
    if (!liveStreams || Object.keys(liveStreams).length === 0) return [];

    const sorted = getSortedThumbnails(liveStreams);
    return sorted
      .map((v) => v.videoId)
      .filter(
        (id) =>
          !currentPlaying.includes(id) &&
          !recentlyEndedIds.includes(id) &&
          !BLOCKED_VIDEO_IDS.has(id)
      );
  };

  useEffect(() => {
    if (liveStreams && Object.keys(liveStreams).length > 0) {
      const availableIds = getAvailableVideoIds([]);

      // Make sure we don't duplicate videos across slots
      const newVideoIds = [...videoIds];
      const currentlyPlaying = new Set(newVideoIds.filter((id) => id));

      for (let i = 0; i < totalPlayers; i++) {
        if (!newVideoIds[i] && availableIds.length > 0) {
          // Find the first available ID that's not already playing
          const nextAvailableId = availableIds.find(
            (id) => !currentlyPlaying.has(id)
          );

          if (nextAvailableId) {
            newVideoIds[i] = nextAvailableId;
            currentlyPlaying.add(nextAvailableId);
          }
        }
      }

      if (JSON.stringify(newVideoIds) !== JSON.stringify(videoIds)) {
        setVideoIds(newVideoIds);
        setCurrentVideoIds(newVideoIds);
      }
    }
  }, [liveStreams]);

  useEffect(() => {
    if (liveStreams) {
      const currentStreamIds = new Set(
        Object.values(liveStreams)
          .filter((data) => data?.videoId)
          .map((data) => data.videoId)
      );

      setAllThumbnails(getSortedThumbnails(liveStreams));

      const updatedVideoIds = [...videoIds];
      let needsUpdate = false;

      for (let i = 0; i < updatedVideoIds.length; i++) {
        const id = updatedVideoIds[i];
        if (id && !currentStreamIds.has(id)) {
          updatedVideoIds[i] = "";
          needsUpdate = true;
        }
      }

      if (needsUpdate) {
        setVideoIds(updatedVideoIds);
        setCurrentVideoIds(updatedVideoIds);

        const availableIds = getAvailableVideoIds(
          updatedVideoIds.filter((id) => id)
        );
        const finalVideoIds = [...updatedVideoIds];

        for (let i = 0; i < finalVideoIds.length; i++) {
          if (!finalVideoIds[i] && availableIds.length > 0) {
            finalVideoIds[i] = availableIds.shift();
          }
        }

        if (JSON.stringify(finalVideoIds) !== JSON.stringify(updatedVideoIds)) {
          setVideoIds(finalVideoIds);
          setCurrentVideoIds(finalVideoIds);
        }
      }
    }
  }, [liveStreams]);

  const handleSelectIndex = (index) => {
    setSelectedIndex(index);
    hasInteracted.current = true;
    setEndedIndexes((prev) => prev.filter((i) => i !== index));
  };

  const handleDoubleTap = (index) => {
    const now = Date.now();
    const DOUBLE_TAP_DELAY = 400;
    if (now - lastTapRef.current < DOUBLE_TAP_DELAY) {
      setFullscreenIndex((prev) => (prev === index ? null : index));
    } else {
      console.log("Single Tap");
      setShowVideoThumbnails(true);
      lastTapRef.current = now;
    }
  };

  const handleVideoSelect = (newVideoId) => {
    if (currentVideoIds.includes(newVideoId)) return;

    const updated = [...videoIds];
    updated[selectedIndex] = newVideoId;
    setVideoIds(updated);
    setCurrentVideoIds(updated);
    setEndedIndexes((prev) => prev.filter((i) => i !== selectedIndex));
  };

  const replaceVideoAtIndex = async (index, oldVideoId) => {
    try {
      // Add the ended video ID to the recently ended list
      const newRecentlyEndedIds = [
        ...new Set([...recentlyEndedIds, oldVideoId]),
      ];

      // Create a copy of the current video IDs and mark this slot as empty
      const currentPlaying = [...videoIds];
      currentPlaying[index] = "";

      // Get IDs that are already playing in other slots
      const currentlyPlayingIds = new Set(
        currentPlaying.filter((id, idx) => id && idx !== index)
      );

      // Get available videos excluding currently playing ones
      const availableIds = getAvailableVideoIds(
        currentPlaying.filter((id) => id)
      );

      // Find an available ID that's not already playing in another slot
      const uniqueAvailableId = availableIds.find(
        (id) => !currentlyPlayingIds.has(id)
      );

      // Prepare the final state update
      const updatedVideoIds = [...currentPlaying];
      let updatedEndedIndexes = [...endedIndexes.filter((i) => i !== index)];

      if (uniqueAvailableId) {
        updatedVideoIds[index] = uniqueAvailableId;
      } else {
        updatedEndedIndexes = [...updatedEndedIndexes, index];
      }

      // Update all state in a single render cycle
      setRecentlyEndedIds(newRecentlyEndedIds);
      setVideoIds(updatedVideoIds);
      setCurrentVideoIds(updatedVideoIds);
      setEndedIndexes(updatedEndedIndexes);
    } catch (err) {
      console.error("Video replace error:", err);
    }
  };

  useEffect(() => {
    if (liveStreams) {
      setAllThumbnails(getSortedThumbnails(liveStreams));
    } else {
      setAllThumbnails([]);
    }
  }, [liveStreams]);

  const handleOnVideoEnded = (videoId) => {
    console.log("onVideoEnded", videoId);
    // Add the ended video ID to the ended streams list
    addEndedStream(videoId);

    // Find the index of the video that ended
    // const index = videoIds.findIndex((id) => id === videoId);
    // if (index !== -1) {
    //   replaceVideoAtIndex(index, videoId);
    // }
  };

  // Handle thumbnail focus to prevent focus loops
  const handleThumbnailFocus = (index) => {
    // Skip if we're already in the process of programmatic focus
    if (!isUserFocus.current) return;

    // Don't update if the index is already selected (prevents loops)
    if (index === selectedThumbnailIndex) return;

    // Update the selected index
    setSelectedThumbnailIndex(index);
  };

  // When showing thumbnails, reset the selected thumbnail index
  useEffect(() => {
    if (showVideoThumbnails) {
      // Only reset if we're showing thumbnails for the first time
      setSelectedThumbnailIndex(0);
    }
  }, [showVideoThumbnails]);

  // Update layout when totalPlayers changes
  useEffect(() => {
    // Ensure selected index is valid for the new layout
    if (selectedIndex >= totalPlayers) {
      setSelectedIndex(totalPlayers - 1);
    }
  }, [totalPlayers, selectedIndex]);

  return (
    <div
      style={{
        width: "100vw",
        height: "100vh",
        maxWidth: "100vw",
        maxHeight: "100vh",
        display: "flex",
        justifyContent: "space-around",
        alignItems: "center",
        padding: "12px",
        flexWrap: "wrap",
        fontFamily: "Lato",
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        overflow: "hidden",
      }}
    >
      {Array(totalPlayers)
        .fill()
        .map((_, index) => {
          const id = videoIds[index] || "";
          const isSelected = selectedIndex === index;
          const isFullscreen = fullscreenIndex === index;
          const showEndedOverlay = endedIndexes.includes(index) || !id;
          const streamerName = id ? videoIdToStreamer[id] || "" : "";
          const isPreRecorded =
            streamerName && PRE_RECORDED_NAMES.has(streamerName);
          const isLive =
            id &&
            Object.entries(liveStreams || {}).some(
              ([name, data]) =>
                data?.videoId === id && !PRE_RECORDED_NAMES.has(name)
            );

          return (
            <button
              key={index}
              ref={(el) => (videoGridRefs.current[index] = el)}
              // onFocus={() => setSelectedIndex(index)}
              style={{
                height:
                  isFullscreen || totalPlayers === 1
                    ? "100vh"
                    : `calc(${100 / gridSize}% - 12px)`,
                width:
                  isFullscreen || totalPlayers === 1
                    ? "100vw"
                    : `calc(${100 / gridSize}% - 30px)`,
                backgroundColor: "black",
                position:
                  isFullscreen || totalPlayers === 1 ? "absolute" : "relative",
                top: isFullscreen || totalPlayers === 1 ? 0 : 0,
                left: isFullscreen || totalPlayers === 1 ? 0 : 0,
                right: isFullscreen || totalPlayers === 1 ? 0 : 0,
                bottom: isFullscreen || totalPlayers === 1 ? 0 : 0,
                zIndex: isFullscreen || totalPlayers === 1 ? 1000 : 1,
                outline:
                  isSelected && !(isFullscreen || totalPlayers === 1)
                    ? "6px solid #05f651"
                    : "6px solid transparent",
                borderRadius: isFullscreen || totalPlayers === 1 ? 0 : "12px",
                overflow: "hidden",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
              onClick={() => {
                handleSelectIndex(index);
                // handleDoubleTap(index);
                setShowVideoThumbnails(true);
              }}
              tabIndex={!showVideoThumbnails && !showGridConfigModal ? 0 : -1}
            >
              <div className="click-capture" />
              {id ? (
                <YouTubePlayer
                  key={id || index}
                  videoId={id}
                  isSelected={isSelected}
                  onEnded={() => handleOnVideoEnded(id)}
                  onError={() => handleOnVideoEnded(id)}
                />
              ) : (
                <div className="empty-player"></div>
              )}
              {streamerName && (
                <div
                  className="streamer-label"
                  style={{
                    fontSize: 19,
                  }}
                >
                  @{streamerName}
                </div>
              )}
              {isPreRecorded ? (
                <div
                  className="pre-recorded-badge"
                  style={{
                    fontSize: 16,
                  }}
                >
                  PRE-RECORDED
                </div>
              ) : isLive ? (
                <div
                  className="live-badge"
                  style={{
                    fontSize: 16,
                  }}
                >
                  LIVE
                </div>
              ) : null}
              {showEndedOverlay && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "black",
                    color: "white",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontSize: "2.4rem",
                    padding: "20px",
                    textAlign: "center",
                    zIndex: 20,
                  }}
                >
                  {id ? (
                    <>
                      This LIVE has ended.
                      <br />
                      Select Another Stream To Continue...
                    </>
                  ) : (
                    <>
                      No Live Streams Currently Available
                      <br />
                      Check Back Soon!
                    </>
                  )}
                </div>
              )}
            </button>
          );
        })}

      {/* Thumbnails */}
      <div
        id="thumbnail-scroll"
        className="thumbnail-scroll"
        style={{
          position: "absolute",
          bottom: "0",
          left: "0",
          right: "0",
          zIndex: 1000,
          transition: "all 0.3s",
          opacity: showVideoThumbnails ? 1 : 0,
          backgroundColor: "rgba(59, 60, 60, 0.8)",
        }}
      >
        {[
          { videoId: "vip_exit" },
          { videoId: "close" },
          { videoId: "settings" },
          ...allThumbnails,
        ].map((stream, index) => {
          const disabled = currentVideoIds.includes(stream.videoId);
          const isThumbnailSelected = selectedThumbnailIndex === index;

          if (stream.videoId === "vip_exit") {
            return (
              <button
                key={stream.videoId}
                ref={(el) => (thumbnailRefs.current[index] = el)}
                className={`thumb-box ${disabled ? "disabled" : ""} ${
                  isThumbnailSelected ? "selected" : ""
                }`}
                onClick={() => {
                  updateView("VIP", () => {
                    setShowBottomBar(true);
                  });
                }}
                onFocus={() => handleThumbnailFocus(index)}
                style={{
                  outline: isThumbnailSelected
                    ? "6px solid #05f651"
                    : "6px solid white",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "white",
                  background: "transparent",
                  backgroundColor: "red",
                  padding: "8px 12px",
                  border: "none",
                  cursor: "pointer",
                }}
                tabIndex={showVideoThumbnails ? 0 : -1}
              >
                <span
                  style={{
                    fontSize: 34,
                    textAlign: "center",
                    fontWeight: "bold",
                    color: "white",
                  }}
                >
                  VIP
                  <br /> EXIT
                </span>
              </button>
            );
          }

          if (stream.videoId === "close") {
            return (
              <button
                key={stream.videoId}
                ref={(el) => (thumbnailRefs.current[index] = el)}
                className={`thumb-box ${disabled ? "disabled" : ""} ${
                  isThumbnailSelected ? "selected" : ""
                }`}
                onClick={() => {
                  console.log("close");
                  setShowVideoThumbnails(false);
                }}
                onFocus={() => handleThumbnailFocus(index)}
                style={{
                  outline: isThumbnailSelected
                    ? "6px solid #05f651"
                    : "6px solid white",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "white",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
                tabIndex={showVideoThumbnails ? 0 : -1}
              >
                <IoClose size={80} color="white" />
              </button>
            );
          }
          if (stream.videoId === "settings") {
            return (
              <button
                key={stream.videoId}
                ref={(el) => (thumbnailRefs.current[index] = el)}
                className={`thumb-box ${disabled ? "disabled" : ""} ${
                  isThumbnailSelected ? "selected" : ""
                }`}
                onClick={() => {
                  setShowVideoThumbnails(false);
                  setSelectedGridOption(0);
                }}
                onFocus={() => handleThumbnailFocus(index)}
                style={{
                  outline: isThumbnailSelected
                    ? "6px solid #05f651"
                    : "6px solid white",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "white",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
                tabIndex={showVideoThumbnails ? 0 : -1}
              >
                <MdGridView size={80} color="white" />
              </button>
            );
          }

          return (
            <button
              key={stream.videoId}
              ref={(el) => (thumbnailRefs.current[index] = el)}
              className={`thumb-box ${disabled ? "disabled" : ""} ${
                isThumbnailSelected ? "selected" : ""
              }`}
              onClick={() => !disabled && handleVideoSelect(stream.videoId)}
              onFocus={() => handleThumbnailFocus(index)}
              disabled={disabled}
              style={{
                outline: isThumbnailSelected
                  ? "6px solid #05f651"
                  : "6px solid transparent",
                background: "transparent",
                border: "none",
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.6 : 1,
              }}
              tabIndex={showVideoThumbnails ? 0 : -1}
            >
              <img src={stream.thumbnail} alt={stream.title} />
              <div className="thumb-meta">
                <strong
                  style={{
                    maxWidth: "180px",
                    width: "100%",
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: 18,
                  }}
                >
                  @{stream.streamer}
                </strong>
                <div className="badge-row">
                  {stream.isLoop ? (
                    <div
                      className="pre-recorded-badge"
                      style={{ position: "static", fontSize: 16 }}
                    >
                      PRE-RECORDED
                    </div>
                  ) : (
                    <div
                      className="live-badge"
                      style={{ position: "static", fontSize: 14 }}
                    >
                      WATCH LIVE!
                    </div>
                  )}
                </div>
                {disabled && (
                  <div
                    className="playing-label"
                    style={{
                      fontSize: 12,
                    }}
                  >
                    (Currently Playing)
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {showGridConfigModal && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000,
            background: "rgba(255, 255, 255, 0.35)", // soft translucent white
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 20,
            flexDirection: "column",
          }}
        >
          <h2
            style={{
              color: "#ffffff",
              fontSize: "44px",
              marginBottom: "24px",
              fontWeight: 600,
              letterSpacing: "0.5px",
              textAlign: "center",
              textShadow: "0 3px 6px rgba(0, 0, 0, 0.75)",
              background:
                "linear-gradient(90deg, rgba(0,0,0,0.6), rgba(0,0,0,0.2), rgba(0,0,0,0.6))",
              padding: "10px 24px",
              borderRadius: "12px",
            }}
          >
            Select Your Layout
          </h2>

          <div style={{ display: "flex", gap: 20 }}>
            <button
              style={{
                background:
                  selectedGridOption === 0
                    ? "linear-gradient(180deg, #003cff, #00034c)"
                    : "linear-gradient(180deg, #001a66, #00034c)",
                border:
                  selectedGridOption === 0
                    ? "6px solid #05f651"
                    : "6px solid #1a6eff",
                borderRadius: "20px",
                padding: "20px 30px",
                width: "200px",
                height: "160px",
                color: "white",
                fontSize: "24px",
                fontWeight: "bold",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 0 20px rgba(0, 255, 255, 0.3)",
                textShadow: "0 2px 4px rgba(0,0,0,0.6)",
                transform: selectedGridOption === 0 ? "scale(1.1)" : "scale(1)",
                transition: "all 0.2s ease-in-out",
                gap: "12px",
              }}
            >
              {/* Fixed-height icon wrapper for vertical alignment */}
              <div
                style={{
                  height: "60px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {/* 2x2 grid icon */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gridTemplateRows: "1fr 1fr",
                    gap: "6px",
                    width: "50px",
                    height: "50px",
                  }}
                >
                  {Array(4)
                    .fill(0)
                    .map((_, i) => (
                      <div
                        key={i}
                        style={{
                          backgroundColor: "white",
                          borderRadius: "4px",
                        }}
                      />
                    ))}
                </div>
              </div>
              4 Streams
            </button>

            <button
              style={{
                background:
                  selectedGridOption === 1
                    ? "linear-gradient(180deg, #003cff, #00034c)"
                    : "linear-gradient(180deg, #001a66, #00034c)",
                border:
                  selectedGridOption === 1
                    ? "6px solid #05f651"
                    : "6px solid #1a6eff",
                borderRadius: "20px",
                padding: "20px 30px",
                width: "200px",
                height: "160px",
                color: "white",
                fontSize: "24px",
                fontWeight: "bold",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 0 20px rgba(0, 255, 255, 0.3)",
                textShadow: "0 2px 4px rgba(0,0,0,0.6)",
                transform: selectedGridOption === 1 ? "scale(1.1)" : "scale(1)",
                transition: "all 0.2s ease-in-out",
                gap: "12px",
              }}
            >
              {/* Fixed height wrapper for icon alignment */}
              <div
                style={{
                  height: "60px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: "6px",
                }}
              >
                {/* Top row: 2 boxes side by side */}
                <div
                  style={{
                    display: "flex",
                    gap: "6px",
                    justifyContent: "center",
                  }}
                >
                  <div
                    style={{
                      width: "22px",
                      height: "22px",
                      backgroundColor: "white",
                      borderRadius: "4px",
                    }}
                  />
                  <div
                    style={{
                      width: "22px",
                      height: "22px",
                      backgroundColor: "white",
                      borderRadius: "4px",
                    }}
                  />
                </div>
                {/* Bottom row: 1 centered box */}
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <div
                    style={{
                      width: "22px",
                      height: "22px",
                      backgroundColor: "white",
                      borderRadius: "4px",
                    }}
                  />
                </div>
              </div>
              3 Streams
            </button>

            <button
              style={{
                background:
                  selectedGridOption === 2
                    ? "linear-gradient(180deg, #003cff, #00034c)"
                    : "linear-gradient(180deg, #001a66, #00034c)",
                border:
                  selectedGridOption === 2
                    ? "6px solid #05f651"
                    : "6px solid #1a6eff",
                borderRadius: "20px",
                padding: "20px 30px",
                width: "200px",
                height: "160px",
                color: "white",
                fontSize: "24px",
                fontWeight: "bold",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 0 20px rgba(0, 255, 255, 0.3)",
                textShadow: "0 2px 4px rgba(0,0,0,0.6)",
                transform: selectedGridOption === 2 ? "scale(1.1)" : "scale(1)",
                transition: "all 0.2s ease-in-out",
                gap: "12px",
              }}
            >
              {/* Fixed height wrapper for alignment */}
              <div
                style={{
                  height: "60px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {/* Icon: 2 side-by-side blocks */}
                <div
                  style={{
                    display: "flex",
                    gap: "6px",
                  }}
                >
                  <div
                    style={{
                      width: "22px",
                      height: "22px",
                      backgroundColor: "white",
                      borderRadius: "4px",
                    }}
                  />
                  <div
                    style={{
                      width: "22px",
                      height: "22px",
                      backgroundColor: "white",
                      borderRadius: "4px",
                    }}
                  />
                </div>
              </div>
              2 Streams
            </button>

            <button
              style={{
                background:
                  selectedGridOption === 3
                    ? "linear-gradient(180deg, #003cff, #00034c)"
                    : "linear-gradient(180deg, #001a66, #00034c)",
                border:
                  selectedGridOption === 3
                    ? "6px solid #05f651"
                    : "6px solid #1a6eff",
                borderRadius: "20px",
                padding: "20px 30px",
                width: "200px",
                height: "160px",
                color: "white",
                fontSize: "24px",
                fontWeight: "bold",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 0 20px rgba(0, 255, 255, 0.3)",
                textShadow: "0 2px 4px rgba(0,0,0,0.6)",
                transform: selectedGridOption === 3 ? "scale(1.1)" : "scale(1)",
                transition: "all 0.2s ease-in-out",
                gap: "12px",
              }}
            >
              {/* Fixed-height icon wrapper for alignment */}
              <div
                style={{
                  height: "60px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {/* Single centered square */}
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    backgroundColor: "white",
                    borderRadius: "4px",
                  }}
                />
              </div>
              1 Stream
            </button>

            <button
              style={{
                background:
                  selectedGridOption === 4
                    ? "linear-gradient(180deg, #ff4c4c, #800000)"
                    : "linear-gradient(180deg, #a80000, #4d0000)",
                border:
                  selectedGridOption === 4
                    ? "6px solid #05f651"
                    : "6px solid #ff4c4c",
                borderRadius: "20px",
                padding: "20px 30px",
                width: "200px",
                height: "160px",
                color: "white",
                fontSize: "24px",
                fontWeight: "bold",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 0 20px rgba(255, 0, 0, 0.3)",
                textShadow: "0 2px 4px rgba(0,0,0,0.6)",
                transform: selectedGridOption === 4 ? "scale(1.1)" : "scale(1)",
                transition: "all 0.2s ease-in-out",
                gap: "12px",
              }}
            >
              {/* Icon wrapper with centered “X” */}
              <div
                style={{
                  height: "60px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "52px",
                    fontWeight: 900,
                    lineHeight: "1",
                    color: "white",
                  }}
                >
                  ×
                </div>
              </div>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiViewLandingTV;
